import React from 'react';
import {
  Button, Form, Container, ListGroup, Spinner, Alert,
} from 'react-bootstrap';
import TransactionService from '../../../service/TransactionService';
import MixedResultView from './MixedResultView';

class BulkGenResolveView extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      transactions: [],
      submittedTransactionsAreValidUUIDs: false,
      submissionAttempted: false,
      failedUUIDs: [],
      transactionData: [],
      fetchDataInProgress: false,
      notFoundTransactions: [],
      uuidValidationErrorMessage: '',
      cashTransactionsFound: [],
    };
  }

  areAllUuidsValid(uuids) {
    const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;

    const invalidUuids = uuids.filter(uuid => !uuidRegex.test(uuid));

    this.setState({ failedUUIDs: invalidUuids, });

    return invalidUuids.length === 0;
  }

  noDuplicateTransactions(uuids) {
    const uuidCount = uuids.reduce((acc, uuid) => {
      acc[uuid] = (acc[uuid] || 0) + 1;
      return acc;
    }, {});

    const duplicateUuids = Object.keys(uuidCount).filter((uuid) => uuidCount[uuid] > 1);

    this.setState(prevState => ({
      failedUUIDs: [ ...prevState.failedUUIDs, ...duplicateUuids, ],
    }));

    return duplicateUuids;
  }

  validateInputAndFetchTransactionData = () => {
    // after you click "fetch transactions" we should validate again
    this.setState({
      failedUUIDs: [], uuidValidationErrorMessage: '', submittedTransactionsAreValidUUIDs: false, submissionAttempted: true, cashTransactionsFound: [],
    });

    let transactionIds = this.getValidatedTransactionIds();

    if (!Array.isArray(transactionIds)) {
      transactionIds = [ transactionIds, ];
    }

    const areUuidsValid = this.areAllUuidsValid(transactionIds);
    const duplicateUuids = this.noDuplicateTransactions(transactionIds);

    if (areUuidsValid && duplicateUuids.length === 0 && transactionIds.length > 0) {
      this.setState({
        transactions: transactionIds,
        submissionAttempted: true,
        submittedTransactionsAreValidUUIDs: true,
      }, () => {
        this.getV2AndV3TransactionData();
      });
      return;
    }

    let uuidValidationErrorMessage = '';
    if (duplicateUuids.length > 0 || !areUuidsValid) {
      uuidValidationErrorMessage = 'Validation failed with UUID(s). The transaction(s) you entered may be malformed or duplicated.';
    }
    if (transactionIds.length === 0) {
      uuidValidationErrorMessage = 'No UUIDs provided.';
    }

    this.setState({
      submittedTransactionsAreValidUUIDs: false,
      uuidValidationErrorMessage,
    });
  };

  getValidatedTransactionIds() {
    // Ensure transactions is a string before calling replace and split
    const transactionsString = Array.isArray(this.state.transactions) ? this.state.transactions.join(',') : this.state.transactions;
    const cleanedTransactionsString = transactionsString.replace(/\s/g, '');

    if (cleanedTransactionsString === '') {
      return [];
    }

    // trim any trailing commas
    if (cleanedTransactionsString.endsWith(',')) {
      return cleanedTransactionsString.slice(0, -1).split(',');
    }

    return cleanedTransactionsString.split(',');
  }

  filterAndStoreAllCashTransactions = (v3Transactions) => {
    const { cashTransactions, v3TransactionsLessCashTransactions, } = v3Transactions.reduce((acc, transaction) => {
      if (transaction.data.v3transaction.paymentAccountType === 'CASH') {
        acc.cashTransactions.push(transaction.data.v3transaction.id);
      } else {
        acc.v3TransactionsLessCashTransactions.push(transaction);
      }
      return acc;
    }, { cashTransactions: [], v3TransactionsLessCashTransactions: [], });

    if (cashTransactions.length > 0) {
      this.setState({ cashTransactionsFound: cashTransactions, });
    }

    return v3TransactionsLessCashTransactions;
  };

  async getV2AndV3TransactionData() {
    // try to fetch V2 transactions first
    // a transaction is said to be v2 if it contains the field res.data.transaction.id
    // if the transaction does not contain the field rest.data.transaction.id, it might be a v3 transaction
    // a transaction is said to be a v3 if it contains the field res.data.v3transaction.id
    // if the transaction contains neither of the fields, do not add it to the state to be gen_resolved

    this.setState({ fetchDataInProgress: true, });

    const transactionIds = this.state.transactions;
    const notFoundTransactions = [];
    const possibleV3Transactions = [];
    const v2Transactions = [];
    const v3Transactions = [];

    const v2TransactionFetches = await Promise.all(transactionIds
      .map((transactionId) => TransactionService.getTransactionDetail(transactionId)));
    v2TransactionFetches.forEach((res, index) => {
      const transactionId = transactionIds[index];
      if (res.data?.transaction?.id) {
        v2Transactions.push({ ...res, transactionId, transactionType: 'V2', });
      } else {
        possibleV3Transactions.push(transactionId);
      }
    });
    const v3Fetches = await Promise.all(possibleV3Transactions
      .map(transactionId => TransactionService.getV3TransactionDetail(transactionId)));
    v3Fetches.forEach((res, index) => {
      const transactionId = possibleV3Transactions[index];
      if (res.data?.v3transaction?.id) {
        v3Transactions.push({ ...res, transactionId, transactionType: 'V3', });
      } else {
        notFoundTransactions.push(transactionId);
      }
    });

    // cash transactions shouldn't be gen_resolve'd
    const v3TransactionsLessCashTransactions = this.filterAndStoreAllCashTransactions(v3Transactions);
    this.setState({
      transactionData: [ ...v2Transactions, ...v3TransactionsLessCashTransactions, ],
      notFoundTransactions,
      fetchDataInProgress: false,
    });
  }

  resetState = () => {
    this.setState({
      transactions: [],
      submittedTransactionsAreValidUUIDs: false,
      submissionAttempted: false,
      failedUUIDs: [],
      transactionData: {},
      fetchDataInProgress: false,
    });
  };

  renderResultView() {
    return (
      <>
        {
        !this.state.fetchDataInProgress
        && this.state.submittedTransactionsAreValidUUIDs
        && this.state.submissionAttempted
          ? (
            <MixedResultView
              transactionData={this.state.transactionData}
              notFoundTransactions={this.state.notFoundTransactions}
            />
          )
          : null
        }
      </>
    );
  }

  renderCashTransactionAlert() {
    return (
      <>
        {
          this.state.cashTransactionsFound.length > 0 ? (
            <>
              <Alert variant="warning" style={{ marginTop: '20px', }}>
                <span role="img" aria-label="warning">💵</span> {this.state.cashTransactionsFound.length} cash transactions identified and will be omitted from selection.
                <br />
                <span>Excluded CASH transaction ID(s): {this.state.cashTransactionsFound.join(', ')}</span>
              </Alert>
            </>
          ) : (null)
        }
      </>
    );
  }

  renderValidationResult() {
    return (
      <>
        {this.state.submissionAttempted && (
          this.state.submittedTransactionsAreValidUUIDs ? (
            <Alert variant="success" style={{ marginTop: '20px', }}>
              <span role="img" aria-label="green check mark">✅</span> All UUIDs entered are formatted correctly. Count of {this.state.transactions.length} UUIDs entered.
            </Alert>
          ) : (
            <Alert variant="danger" style={{ marginTop: '20px', }}>
              <span role="img" aria-label="red x">❌</span> {this.state.uuidValidationErrorMessage}
              {
                this.state.failedUUIDs.length > 0 && this.state.failedUUIDs[0] !== '' && (
                  <ListGroup>
                    {this.state.failedUUIDs.map((uuid) => (
                      <ListGroup.Item
                        key={uuid}
                        style={{
                          backgroundColor: '#f8f9fa', borderRadius: '5px', padding: '20px', margin: '10px 0', color: '#333',
                        }}
                      >{uuid}
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                )
              }
            </Alert>
          )
        )}
        {this.renderCashTransactionAlert()}
      </>
    );
  }

  renderLoadingSpinner() {
    return (
      <>
        {this.state.fetchDataInProgress
          ? (
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          )
          : null}
      </>
    );
  }

  renderForm() {
    return (
      <div style={{
        backgroundColor: '#f8f9fa', borderRadius: '5px', padding: '20px', margin: '10px 0',
      }}
      >
        <Alert variant="primary">
          <span role="img" aria-label="notepad">📝</span>
          &nbsp;A Confluence page has been published about this tool&apos;s purpose and usage. Read & contribute to it here:
          <a href="https://payitdev.atlassian.net/wiki/x/DIAh-/" target="_blank" rel="noopener noreferrer">
            &nbsp;Using the Bulk gen_resolve Tool within Athos
          </a>
        </Alert>
        <Form.Label style={{ color: '#333', }}>Bulk Transaction Input</Form.Label>
        <p style={{ color: '#6c757d', }}>
          This page supports modifying a transaction&apos;s Service Offering ID(SOID) as
          gen_resolve as well as reverting to the originally issued SOID.
          If you require integration data to have its SOID changed, please contact an engineer with database edit access.
        </p>
        <Form.Control
          as="textarea"
          value={this.state.transactions}
          onChange={e => this.setState({ transactions: e.target.value, submissionAttempted: false, cashTransactionsFound: [], })}
          placeholder="Enter transaction IDs with format: transaction_one, transaction_two ..."
          rows={4}
          style={{ marginBottom: '20px', }}
        />
        <Button variant="primary" onClick={this.validateInputAndFetchTransactionData}>Fetch transactions</Button>
        {this.renderValidationResult()}
      </div>
    );
  }

  render() {
    return (
      <Container style={{ alignItems: 'flex-start', }}>
        {this.renderForm()}
        {this.renderLoadingSpinner()}
        {this.renderResultView()}
      </Container>
    );
  }
}

export default BulkGenResolveView;
